'use client';

import React, { useEffect, useState } from 'react';

import { Button } from 'lighthouse';

import { calculateSatisfaction } from '@/utilities/calculateSatisfaction';

import { useSupportStore } from '@/stores/support/useSupportStore';

import {
    getBrandShippingContent,
    getBrandTrustPanelItems,
    getBrandTrustPoints,
    heroCopy,
    heroTrustPanelCopy,
    landingStatsDefaults,
    landingStatsLabels,
    PRODUCTS_SECTION_ID
} from '@/constants/brandLanding';

import classes from './index.module.css';
import { BrandHeroProps, BrandHeroStat } from './models';

import { Call, MedalStar, Shop, TickCircle } from 'iconsax-react';

const useCountUp = (target: number, duration = 1600) => {
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setValue(target);
            return;
        }

        let frame: number;
        const start = performance.now();
        const tick = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(eased * target));
            if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [target, duration]);

    return value;
};

const StatCounter = ({ value, suffix, label }: BrandHeroStat) => {
    const count = useCountUp(value);
    return (
        <div className='flex flex-col items-center gap-1 px-2 py-4'>
            <span
                className={`text-2xl font-extrabold md:text-3xl
                    ${suffix === '٪' ? calculateSatisfaction(value / 20, 1).textClassName : 'text-blackHighEmphasis'}`}>
                {count.toLocaleString('fa-IR')}
                <span className={suffix === '٪' ? 'text-current' : 'text-primary'}>{suffix}</span>
            </span>
            <span className='text-xs text-blackMediumEmphasis md:text-sm'>{label}</span>
        </div>
    );
};

const BrandTrustPanel = ({ brandName, productCount, shippingScope }: Pick<BrandHeroProps, 'brandName' | 'productCount' | 'shippingScope'>) => {
    const itemDelayClass = [classes.trustItemDelay1, classes.trustItemDelay2, classes.trustItemDelay3];
    const checkDelayClass = [classes.trustCheckDelay1, classes.trustCheckDelay2, classes.trustCheckDelay3];
    const panelItems = getBrandTrustPanelItems(shippingScope);

    return (
        <div className='animate-float relative z-10 mx-auto w-full max-w-sm md:max-w-md'>
            <div
                className={`${classes.trustPanel} relative overflow-hidden rounded-lg border border-primary/10 bg-white p-6 shadow-elevation2 md:p-8`}
                dir='rtl'>
                <div className={`${classes.trustGlow} pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full`} />
                <div className={`${classes.trustSheen} pointer-events-none absolute inset-0`} />
                <div className='pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-l from-transparent via-primary/30 to-transparent' />

                <div className='relative text-center'>
                    <span
                        className='mb-4 inline-flex items-center gap-1.5 rounded-full border border-primary/15 bg-primary/10 px-3 py-1 text-[11px]
                            font-medium text-primary'>
                        <MedalStar size='13' variant='Bold' color='currentColor' />
                        {heroTrustPanelCopy.badge}
                    </span>
                    <p className='text-xl font-black text-blackHighEmphasis md:text-2xl'>{brandName}</p>
                    <p className='mt-2 text-sm text-blackMediumEmphasis'>
                        {productCount > 0 ? `${productCount.toLocaleString('fa-IR')} محصول اصل با موجودی به‌روز` : 'محصولات اصل با موجودی به‌روز'}
                    </p>
                </div>

                <div className='relative mt-6 space-y-3'>
                    {panelItems.map((item, index) => (
                        <div
                            key={item.text}
                            className={`${classes.trustItem} ${itemDelayClass[index] || ''} flex items-center justify-between rounded-lg bg-sky-50/80
                            px-4 py-3`}>
                            <span className='inline-flex items-center gap-2 text-sm text-blackMediumEmphasis'>
                                <item.icon size='18' variant='Bulk' className='text-primary' color='currentColor' />
                                {item.text}
                            </span>
                            <span className={`${classes.trustCheck} ${checkDelayClass[index] || ''} inline-flex`}>
                                <TickCircle size='18' variant='Bold' className='text-emerald-500' color='currentColor' />
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const scrollToProducts = () => {
    if (typeof document !== 'undefined') {
        document.getElementById(PRODUCTS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
};

const BrandHero = ({ brandName, pageTitle, descriptionPreview, productCount, shippingScope }: BrandHeroProps) => {
    const openSupport = useSupportStore((store) => store.openSupport);

    const shipping = getBrandShippingContent(shippingScope);
    const trustPoints = getBrandTrustPoints(shippingScope);
    const stats: BrandHeroStat[] = [
        { value: productCount, suffix: productCount > 0 ? '+' : '', label: landingStatsLabels.products },
        { value: shipping.citiesStat, suffix: shipping.citiesStatSuffix, label: landingStatsLabels.cities },
        { value: landingStatsDefaults.satisfaction, suffix: '٪', label: landingStatsLabels.satisfaction }
    ];

    return (
        <section className='relative flex flex-col overflow-hidden bg-gradient-to-b from-sky-50 via-white to-white md:h-[calc(100dvh-4rem)]'>
            <div className='pointer-events-none absolute inset-0'>
                <div className='absolute -top-24 right-0 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl' />
                <div className='absolute -bottom-16 left-0 h-80 w-80 rounded-full bg-sky-200/40 blur-3xl' />
                <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,143,255,0.08),_transparent_55%)]' />
                <div
                    className='absolute inset-0 opacity-70'
                    style={{
                        backgroundImage: 'radial-gradient(rgba(0,143,255,0.22) 1.2px, transparent 1.2px)',
                        backgroundSize: '22px 22px'
                    }}
                />
            </div>

            <div className='desktop-width relative flex min-h-0 w-full flex-1 flex-col px-4 py-10 md:py-4 lg:py-6'>
                <div className='grid min-h-0 flex-1 items-center gap-12 md:grid-cols-2 md:gap-16 lg:gap-20'>
                    <div className='text-center md:text-right'>
                        <h1
                            className={`${classes.heroEntrance} mb-8 mt-2 text-3xl font-black leading-[1.5] text-blackHighEmphasis md:text-[3.1rem]
                                md:leading-[1.4]`}>
                            {pageTitle ? (
                                pageTitle
                            ) : (
                                <>
                                    خرید محصولات
                                    <span className='mt-1 block text-primary'>{brandName}</span>
                                </>
                            )}
                        </h1>

                        <p
                            className={`${classes.heroEntrance} ${classes.heroEntranceDelay1} mx-auto mt-0.5 max-w-xl text-sm leading-8
                                text-blackMediumEmphasis md:mx-0 md:mt-0.5 md:text-sm md:leading-8 lg:text-base lg:leading-9`}>
                            {descriptionPreview || shipping.defaultDescription(brandName)}
                        </p>

                        <div
                            className={`${classes.heroEntrance} ${classes.heroEntranceDelay2} mt-5 flex flex-col items-center gap-3 sm:flex-row
                                sm:flex-wrap sm:justify-center md:mt-6 md:justify-start`}>
                            <Button
                                variant='contained'
                                color='primary'
                                size='large'
                                width='wide'
                                onClick={scrollToProducts}
                                startIcon={<Shop size='20' variant='Bold' color='currentColor' />}
                                className='w-full cursor-pointer text-white sm:w-auto'>
                                {heroCopy.primaryCta}
                            </Button>
                            <Button
                                onClick={() => openSupport('landing_hero')}
                                variant='outlined'
                                color='primary'
                                size='large'
                                width='wide'
                                startIcon={<Call size='20' color='currentColor' />}
                                className='w-full cursor-pointer sm:w-auto'>
                                {heroCopy.secondaryCta}
                            </Button>
                        </div>

                        <div
                            className={`${classes.heroEntrance} ${classes.heroEntranceDelay3} mt-4 flex flex-wrap items-center justify-center gap-x-5
                                gap-y-2 text-xs text-blackMediumEmphasis md:justify-start`}>
                            {trustPoints.map((point) => (
                                <span key={point.text} className='inline-flex items-center gap-1.5'>
                                    <point.icon size='16' variant='Bold' className='text-primary' color='currentColor' />
                                    {point.text}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div
                        className={`${classes.heroEntrance} ${classes.heroEntranceDelay2} relative mx-auto hidden w-full max-w-md sm:block
                            xl:max-w-lg`}>
                        <div className='absolute inset-0 flex items-center justify-center'>
                            <div className={`hero-radar h-72 w-72 rounded-full border border-primary/20 xl:h-96 xl:w-96 ${classes.trustRingSoft}`} />
                            <div
                                className={`hero-radar hero-radar-delay absolute h-56 w-56 rounded-full border border-sky-300/40 xl:h-72 xl:w-72
                                    ${classes.trustRingSoftDelay}`}
                            />
                        </div>
                        <BrandTrustPanel brandName={brandName} productCount={productCount} shippingScope={shippingScope} />
                    </div>
                </div>

                <div
                    className='mt-10 grid grid-cols-3 divide-x divide-x-reverse divide-primary/10 rounded-lg border border-primary/10 bg-white/80
                        shadow-elevation1 backdrop-blur md:mt-4'>
                    {stats.map((stat) => (
                        <StatCounter key={stat.label} {...stat} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default BrandHero;
